import {EditOutlined} from '@ant-design/icons/lib';
import {Alert, Button, Card, Space} from 'antd';
import {AccountPayoutTerms, BillingPlans, UpdateAccountInput} from 'api/generated';
import {AnchorLink, Hint, IBANInput, PropertyList, Stack} from 'components';
import {Formik} from 'formik';
import {Form, FormItem, Input, InputNumber, Select, SubmitButton, Switch} from 'formik-antd';
import {useAccessLevel} from 'hooks/useAccessLevel';
import * as iban from 'ibantools';
import {formatBoolean, formatIBAN, formatPercent} from 'lib/formaters';
import {omitEmpty} from 'lib/omitEmpty';
import React, {useState} from 'react';
import * as yup from 'yup';
import {SectionProps} from '../types';

const PAYOUTS_DEFAULTS: Record<BillingPlans, AccountPayoutTerms> = {
  [BillingPlans.MONEI_POS]: {
    firstPeriod: 1,
    period: 1,
    reservePercent: 0,
    releaseReserve: false,
    suspended: false
  },
  [BillingPlans.MONEI_X]: {
    firstPeriod: 30,
    period: 7,
    reservePercent: 10,
    releaseReserve: false,
    suspended: false
  },
  [BillingPlans.MONEI_PLUS]: {
    firstPeriod: 30,
    period: 7,
    reservePercent: 10,
    releaseReserve: false,
    suspended: false
  }
};

const {Option} = Select;

export const PayoutDetails: React.FC<SectionProps> = ({
  cardProps,
  formProps,
  account,
  onSubmit
}) => {
  const [isEditing, setEditing] = useState(false);
  const {isAdmin} = useAccessLevel();

  if (!account) return <Card {...cardProps} title="Payout details" />;

  const payoutTerms = {
    ...PAYOUTS_DEFAULTS[account.billingPlan as BillingPlans],
    ...omitEmpty(account.payoutTerms || {}, {omitZero: false}),
    settleGross: account.payoutTerms?.settleGross || false
  };

  const initialValues: UpdateAccountInput = {
    id: account.id,
    payoutTerms
  };

  const validationSchema = yup.object().shape({
    payoutTerms: yup.object().shape({
      iban: yup
        .string()
        .test('payoutTerms.iban', 'invalid IBAN', (value) => !value || iban.isValidIBAN(value!))
        .nullable()
    })
  });

  return (
    <Card
      {...cardProps}
      title="Payouts settings"
      extra={
        isEditing || !isAdmin ? null : (
          <AnchorLink onClick={() => setEditing(true)}>
            <EditOutlined /> Edit data
          </AnchorLink>
        )
      }>
      {isEditing ? (
        <Formik
          validationSchema={validationSchema}
          onSubmit={async (values) => {
            await onSubmit(values);
            setEditing(false);
          }}
          initialValues={initialValues}>
          <Form {...formProps}>
            <FormItem name="payoutTerms.iban" label="IBAN">
              <IBANInput name="payoutTerms.iban" countryCode={account.business?.country || 'ES'} />
            </FormItem>
            <FormItem name="payoutTerms.firstPeriod" label="First payout period">
              <InputNumber name="payoutTerms.firstPeriod" min={1} addonAfter="days" />
            </FormItem>
            <FormItem name="payoutTerms.period" label="Payout period">
              <InputNumber name="payoutTerms.period" min={1} addonAfter="days" />
            </FormItem>
            <FormItem name="payoutTerms.reservePercent" label="Payout rolling reserve">
              <InputNumber name="payoutTerms.reservePercent" min={0} addonAfter="%" />
            </FormItem>
            <FormItem
              name="payoutTerms.settleGross"
              label={
                <Space>
                  <span>Payout type</span>
                  <Hint message="For gross payouts the acquiring fees will be charged in the next invoice" />
                </Space>
              }>
              <Select name="payoutTerms.settleGross">
                <Option key={1} value={true}>
                  Gross volume
                </Option>
                <Option key={2} value={false}>
                  Net volume
                </Option>
              </Select>
            </FormItem>
            <FormItem
              name="payoutTerms.bankTransferConceptPrefix"
              label={
                <Space>
                  <span>Bank transfer concept prefix</span>
                  <Hint
                    message={
                      <>
                        <p>
                          This text will be added to the concept of the bank transfer of the payout,
                          being:
                        </p>
                        <p>
                          <strong>{'{{your-text}}'}</strong> - MONEI transfer to{' '}
                          <strong>{'{{legal-name}}'}</strong> -{' '}
                          <strong>{'{{payout-completion - date}}'}</strong> -{' '}
                          <strong>{'{{payment-method}}'}</strong>
                        </p>
                      </>
                    }
                  />
                </Space>
              }>
              <Input name="payoutTerms.bankTransferConceptPrefix" />
            </FormItem>
            <FormItem
              name="payoutTerms.suspended"
              label={
                <Space>
                  <span>Suspend payouts</span>
                  <Hint message="Payouts for this merchant will not be included in the bank transfer file" />
                </Space>
              }>
              <Switch name="payoutTerms.suspended" />
            </FormItem>
            <FormItem
              name="payoutTerms.releaseReserve"
              label={
                <Space>
                  <span>Release reserve</span>
                  <Hint message="The reserved amount will be released in the next payout" />
                </Space>
              }>
              <Switch name="payoutTerms.releaseReserve" />
            </FormItem>
            <FormItem
              name="payoutTerms.settleToStores"
              label={
                <Space>
                  <span>Settle to stores</span>
                  <Hint message="Aggregates the payouts by store and settles to the IBAN of the store" />
                </Space>
              }>
              <Switch name="payoutTerms.settleToStores" />
            </FormItem>
            <FormItem
              wrapperCol={{offset: formProps.labelCol?.span, ...formProps.wrapperCol}}
              name="submit">
              <Stack>
                <Button onClick={() => setEditing(false)}>Cancel</Button>
                <SubmitButton>Submit</SubmitButton>
              </Stack>
            </FormItem>
          </Form>
        </Formik>
      ) : (
        <>
          <PropertyList
            omitEmpty
            labelWidth={280}
            properties={[
              {
                label: (
                  <Hint
                    message={
                      account.payoutTerms?.iban
                        ? 'Account has different IBAN for payouts and billing'
                        : 'Account has the same IBAN for payouts and billing'
                    }
                    title="Payouts IBAN"
                  />
                ),
                copyable: {
                  text: account.payoutTerms?.iban || account.iban || undefined
                },
                value: formatIBAN(account.payoutTerms?.iban || account.iban, true)
              },
              {
                label: 'First payout period in days',
                value: payoutTerms.firstPeriod
              },
              {
                label: 'Payout period in days',
                value: payoutTerms.period
              },
              {
                label: 'Payout rolling reserve',
                value: formatPercent(payoutTerms?.reservePercent)
              },
              {
                label: (
                  <Hint
                    message={
                      'For gross payouts the acquiring fees will be charged in the next invoice'
                    }
                    title="Payout type"
                  />
                ),
                value: payoutTerms.settleGross ? 'Gross volume' : 'Net volume'
              },
              {
                label: (
                  <Hint
                    message={
                      <>
                        <p>
                          This text will be added to the concept of the bank transfer of the payout,
                          being:
                        </p>
                        <p>
                          <strong>{'{{your-text}}'}</strong> - MONEI transfer to{' '}
                          <strong>{'{{legal-name}}'}</strong> -{' '}
                          <strong>{'{{payout-completion - date}}'}</strong> -{' '}
                          <strong>{'{{payment-method}}'}</strong>
                        </p>
                      </>
                    }
                    title="Bank transfer concept prefix"
                  />
                ),
                value: payoutTerms.bankTransferConceptPrefix
              },
              {
                label: (
                  <Hint
                    message={'Aggregates the payouts by store and settles to the IBAN of the store'}
                    title="Settle to stores"
                  />
                ),
                value: formatBoolean(payoutTerms.settleToStores)
              }
            ]}
          />
          {account.payoutTerms?.suspended && (
            <Alert
              style={{marginTop: 20}}
              message="Payouts are suspended for this account. It will not be included in the bank transfer file."
              type="error"
            />
          )}
          {account.payoutTerms?.releaseReserve && (
            <Alert
              style={{marginTop: 20}}
              message="The reserved amount will be released in the next payout."
              type="warning"
            />
          )}
        </>
      )}
    </Card>
  );
};
